import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, ShieldAlert } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CategoryIcon, Spinner } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const LOCATIONS = ["Dubai Marina", "Downtown Dubai", "Dubai Creek Harbour", "Business Bay", "Palm Jumeirah", "Dubai Hills Estate", "Arabian Ranches"];

const DynamicField = ({ field, value, onChange }) => {
  const common = { "data-testid": `job-field-${field.key}` };
  if (field.type === "single_choice")
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className="mt-1.5" {...common}><SelectValue placeholder="Select..." /></SelectTrigger>
        <SelectContent>{field.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    );
  if (field.type === "boolean")
    return <label className="flex items-center gap-2 mt-2 text-sm text-[#4A5568]"><Checkbox checked={!!value} onCheckedChange={onChange} {...common} /> Yes</label>;
  if (field.type === "number")
    return <Input type="number" value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5" {...common} />;
  if (field.type === "date")
    return <Input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5" {...common} />;
  return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5" {...common} />;
};

export default function PostJobPage() {
  const { user, ready, verifyEmail } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [service, setService] = useState(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ location: "", title: "", description: "", urgency: "standard", preferred_date: "", budget: "", answers: {} });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  if (!ready) return <Spinner />;
  if (!user) { navigate("/login"); return null; }

  const pickService = (c) => { setService(c); setForm({ ...form, title: c.name + " needed" }); setStep(2); };
  const setAnswer = (k, v) => setForm({ ...form, answers: { ...form.answers, [k]: v } });

  const submit = async () => {
    if (!user.email_verified) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/jobs", {
        service_slug: service.slug, location: form.location, title: form.title,
        description: form.description, answers: form.answers, urgency: form.urgency,
        preferred_date: form.preferred_date, budget: form.budget,
      });
      toast.success(`Job posted — ${data.matched_companies} matching companies notified`);
      navigate("/dashboard/jobs");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const steps = ["Service", "Details", "Review"];

  return (
    <div className="propora-container py-10 max-w-3xl" data-testid="post-job-page">
      <h1 className="font-display font-extrabold text-4xl text-[#0B1733]">Post a job</h1>
      <p className="text-[#4A5568] mt-2">Tell us what you need. Matching listed companies can respond with quotes. Your contact details and exact address stay private.</p>

      <div className="mt-6 flex items-center gap-2">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step > i + 1 ? "bg-[#007F83] text-white" : step === i + 1 ? "bg-[#0B1733] text-white" : "bg-white border border-[#E3E8E8] text-[#4A5568]"}`}>
              {step > i + 1 ? <Check size={16} /> : i + 1}
            </div>
            <span className={`text-sm ${step === i + 1 ? "font-semibold text-[#0B1733]" : "text-[#4A5568]"}`}>{s}</span>
            {i < steps.length - 1 && <div className="w-8 h-px bg-[#E3E8E8]" />}
          </div>
        ))}
      </div>

      {!user.email_verified && (
        <div className="mt-6 p-4 rounded-2xl bg-amber-50 border border-amber-200 flex items-start gap-3" data-testid="verify-email-banner">
          <ShieldAlert className="text-amber-600 shrink-0" size={20} />
          <div className="text-sm text-amber-800">
            <b>Verify your email to publish.</b> You can prepare your job now. Outgoing email is in development mode, so you can verify instantly here.
            <Button size="sm" onClick={async () => { await verifyEmail(); toast.success("Email verified"); }} data-testid="verify-email-btn" className="ml-2 mt-2 h-8 rounded-full bg-[#007F83] hover:bg-[#006B70] text-white">Verify now</Button>
          </div>
        </div>
      )}

      <div className="mt-6 bg-white rounded-3xl border border-[#E3E8E8] p-7 shadow-soft">
        {step === 1 && (
          <div>
            <h2 className="font-display font-bold text-xl text-[#0B1733] mb-4">What service do you need?</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {cats.map((c) => (
                <button key={c.slug} onClick={() => pickService(c)} data-testid={`service-pick-${c.slug}`}
                  className="flex items-center gap-3 p-4 rounded-xl border border-[#E3E8E8] hover:border-[#007F83] hover:bg-[#E8F5F1] transition-colors text-left">
                  <CategoryIcon name={c.icon} size={22} className="text-[#007F83] shrink-0" />
                  <div><p className="font-semibold text-[#0B1733] text-sm">{c.name}</p><p className="text-xs text-[#4A5568]">{c.short_description}</p></div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && service && (
          <div className="space-y-5">
            <h2 className="font-display font-bold text-xl text-[#0B1733]">{service.name} — details</h2>
            <div><Label>Job title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="job-title-input" className="mt-1.5" /></div>
            <div><Label>Location / area *</Label>
              <Select value={form.location} onValueChange={(v) => setForm({ ...form, location: v })}>
                <SelectTrigger className="mt-1.5" data-testid="job-location-select"><SelectValue placeholder="Select area" /></SelectTrigger>
                <SelectContent>{LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {(service.intake_fields || []).map((f) => (
              <div key={f.key}>
                <Label>{f.label}{f.required && " *"}</Label>
                <DynamicField field={f} value={form.answers[f.key]} onChange={(v) => setAnswer(f.key, v)} />
              </div>
            ))}
            <div><Label>Describe your project *</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} data-testid="job-description-input" className="mt-1.5" /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Urgency</Label>
                <Select value={form.urgency} onValueChange={(v) => setForm({ ...form, urgency: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="job-urgency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="emergency">Emergency</SelectItem>
                    <SelectItem value="this_week">This week</SelectItem>
                    <SelectItem value="standard">Flexible</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Budget (optional)</Label><Input value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} placeholder="AED" data-testid="job-budget-input" className="mt-1.5" /></div>
            </div>
            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(1)} className="rounded-full"><ArrowLeft size={16} className="mr-1" /> Back</Button>
              <Button onClick={() => { if (!form.location || !form.description) { toast.error("Please add location and description"); return; } setStep(3); }} data-testid="job-next-btn" className="rounded-full bg-[#007F83] hover:bg-[#006B70] text-white">Review <ArrowRight size={16} className="ml-1" /></Button>
            </div>
          </div>
        )}

        {step === 3 && service && (
          <div>
            <h2 className="font-display font-bold text-xl text-[#0B1733] mb-4">Review & submit</h2>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between border-b border-[#E3E8E8] pb-2"><dt className="text-[#4A5568]">Service</dt><dd className="font-semibold text-[#0B1733]">{service.name}</dd></div>
              <div className="flex justify-between border-b border-[#E3E8E8] pb-2"><dt className="text-[#4A5568]">Area</dt><dd className="font-semibold text-[#0B1733]">{form.location}</dd></div>
              {Object.entries(form.answers).map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-[#E3E8E8] pb-2"><dt className="text-[#4A5568] capitalize">{k.replace(/_/g, " ")}</dt><dd className="font-semibold text-[#0B1733]">{String(v)}</dd></div>
              ))}
            </dl>
            <p className="mt-4 p-3 rounded-xl bg-[#E8F5F1] text-xs text-[#0B1733]">Public job cards show only the general area, safe scope and category. Your exact address and contact details remain private until you choose to share them.</p>
            <div className="flex justify-between pt-5">
              <Button variant="outline" onClick={() => setStep(2)} className="rounded-full"><ArrowLeft size={16} className="mr-1" /> Back</Button>
              <Button onClick={submit} disabled={submitting || !user.email_verified} data-testid="job-submit-btn" className="rounded-full bg-[#007F83] hover:bg-[#006B70] text-white px-7">
                {submitting ? "Posting..." : "Publish job"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
